/**
 * PeopleMultiSelect — multi-person picker с чекбоксами, используется в rail
 * для атрибутов с attribute_type='reference_list', target_object_type='person'
 * (например, project.members).
 *
 * Триггер показывает стэк аватарок (до 2) + счётчик. По клику открывается
 * поповер со списком людей, разделённых на «В проекте» и «Другие пользователи».
 * Клик по строке тогглит вхождение в массив.
 *
 * Работает с person.id (records.id person-объекта). Backend сохраняет значение
 * в records.attributes.<slug> = ["<person-uuid>", ...].
 */
import { useEffect, useMemo, useState, type ReactNode } from "react"
import { HugeiconsIcon } from "@hugeicons/react"
import { Tick02Icon } from "@hugeicons/core-free-icons"
import { usePeople, type Person } from "@/api/people/usePeople"
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"

type PeopleMultiSelectProps = {
  value: string[] | null
  onCommit: (next: string[] | null) => void
  className?: string
  disabled?: boolean
  /** Триггер: cell (по умолчанию) или chip (для create-форм). */
  triggerVariant?: "cell" | "chip"
  /** Иконка слева (только в chip-варианте). */
  leadingIcon?: ReactNode
  /** Текст-плейсхолдер, когда ничего не выбрано (только в chip-варианте). */
  placeholder?: string
}

function normalizeIds(value: string[] | null | undefined): string[] {
  if (!value) return []
  return value.filter((id): id is string => typeof id === "string" && id.length > 0)
}

export function PeopleMultiSelect({
  value,
  onCommit,
  className,
  disabled,
  triggerVariant = "cell",
  leadingIcon,
  placeholder,
}: PeopleMultiSelectProps) {
  const { people, peopleById, isLoading } = usePeople()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [pending, setPending] = useState(false)

  const selectedIds = useMemo(() => new Set(normalizeIds(value)), [value])

  useEffect(() => {
    if (!open) setQuery("")
  }, [open])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return people
    return people.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.email?.toLowerCase().includes(q) ?? false),
    )
  }, [people, query])

  const ordered = useMemo(() => {
    const inProject: Person[] = []
    const others: Person[] = []
    const presentIds = new Set<string>()

    for (const person of filtered) {
      if (selectedIds.has(person.id)) inProject.push(person)
      else others.push(person)
      presentIds.add(person.id)
    }

    // Если в selectedIds есть UUID, которого нет в `people` (например, человек
    // ещё не загрузился или был удалён), всё равно показываем его в «В проекте»
    // как «неизвестного участника» — чтобы галочка не пропадала и пользователь
    // видел, что в проекте есть кто-то.
    for (const id of selectedIds) {
      if (!presentIds.has(id)) {
        inProject.push({
          id,
          name: "Удалённый участник",
          isInvited: false,
        })
      }
    }

    return { inProject, others }
  }, [filtered, selectedIds])

  async function togglePerson(person: Person) {
    setPending(true)
    try {
      const next = new Set(selectedIds)
      if (next.has(person.id)) next.delete(person.id)
      else next.add(person.id)
      onCommit(next.size > 0 ? Array.from(next) : null)
    } finally {
      setPending(false)
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverAnchor asChild>
        <button
          type="button"
          onClick={() => !disabled && setOpen((v) => !v)}
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            triggerVariant === "chip"
              ? "group/chip inline-flex h-7 max-w-full items-center gap-1.5 rounded-full border border-border/80 bg-background px-2.5 text-left text-xs leading-none font-normal text-foreground/80 transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 data-[open=true]:bg-muted/60 data-[open=true]:text-foreground"
              : "flex h-6 w-full min-w-0 items-center gap-1.5 rounded px-1.5 text-left text-[12.5px] leading-none font-normal transition-colors hover:bg-muted/70 focus:bg-muted/70 focus:outline-none data-[open=true]:bg-muted/70",
            disabled && "pointer-events-none opacity-100",
            triggerVariant === "chip" && selectedIds.size === 0 && "text-muted-foreground/80",
            className,
          )}
          data-open={open}
        >
          {triggerVariant === "chip" && leadingIcon ? (
            <span className="shrink-0 text-muted-foreground [&_svg]:size-3.5">
              {leadingIcon}
            </span>
          ) : null}
          <Trigger
            selectedIds={selectedIds}
            peopleById={peopleById}
            isLoading={isLoading}
            triggerVariant={triggerVariant}
            placeholder={placeholder}
          />
        </button>
      </PopoverAnchor>
      <PopoverContent align="start" className="w-72 p-0">
        <Body
          isLoading={isLoading}
          query={query}
          onQueryChange={setQuery}
          inProject={ordered.inProject}
          others={ordered.others}
          selectedIds={selectedIds}
          pending={pending}
          onToggle={togglePerson}
        />
      </PopoverContent>
    </Popover>
  )
}

function Trigger({
  selectedIds,
  peopleById,
  isLoading,
  triggerVariant,
  placeholder,
}: {
  selectedIds: Set<string>
  peopleById: Map<string, Person>
  isLoading: boolean
  triggerVariant?: "cell" | "chip"
  placeholder?: string
}) {
  if (isLoading) {
    return <span className="text-muted-foreground/40">…</span>
  }
  const count = selectedIds.size
  if (count === 0) {
    if (triggerVariant === "chip") {
      return <span className="truncate">{placeholder ?? "Не выбрано"}</span>
    }
    return null
  }

  if (count === 1) {
    const id = Array.from(selectedIds)[0]!
    const person = peopleById.get(id)
    const name = person?.name ?? ""
    const avatarUrl = person?.avatarUrl
    return (
      <>
        {avatarUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={avatarUrl}
            alt=""
            className="size-4 shrink-0 rounded-full object-cover"
          />
        ) : (
          <span className="inline-flex size-4 shrink-0 items-center justify-center rounded-full bg-amber-500 text-[9px] font-medium text-white">
            {name.slice(0, 1).toUpperCase() || "·"}
          </span>
        )}
        <span className="truncate">{name}</span>
      </>
    )
  }

  const ids = Array.from(selectedIds).slice(0, 2)
  return (
    <>
      <span className="flex shrink-0 -space-x-1.5">
        {ids.map((id) => {
          const person = peopleById.get(id)
          const name = person?.name ?? ""
          const avatarUrl = person?.avatarUrl
          return (
            <span key={id} className="inline-flex shrink-0" title={name}>
              {avatarUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={avatarUrl}
                  alt=""
                  className="size-4 rounded-full object-cover ring-1 ring-background"
                />
              ) : (
                <span className="inline-flex size-4 items-center justify-center rounded-full bg-amber-500 text-[9px] font-medium text-white ring-1 ring-background">
                  {name.slice(0, 1).toUpperCase() || "·"}
                </span>
              )}
            </span>
          )
        })}
      </span>
      <span className="truncate text-muted-foreground/80">
        {count} {pluralizeMembers(count)}
      </span>
    </>
  )
}

function Body({
  isLoading,
  query,
  onQueryChange,
  inProject,
  others,
  selectedIds,
  pending,
  onToggle,
}: {
  isLoading: boolean
  query: string
  onQueryChange: (q: string) => void
  inProject: Person[]
  others: Person[]
  selectedIds: Set<string>
  pending: boolean
  onToggle: (person: Person) => void
}) {
  if (isLoading) {
    return (
      <div className="space-y-1 p-2">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-7 w-full rounded-md" />
        ))}
      </div>
    )
  }
  return (
    <div>
      <div className="border-b p-1">
        <Input
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Поиск..."
          className="h-7 px-2 text-[12px] font-normal border-input/60 focus-visible:border-input focus-visible:ring-0 focus-visible:ring-offset-0"
          autoFocus
        />
      </div>
      <ScrollArea className="max-h-72">
        <div className="p-1 space-y-0.5">
          {inProject.length > 0 && <GroupLabel>В проекте</GroupLabel>}
          {inProject.map((person) => (
            <PersonRow
              key={person.id}
              person={person}
              checked={true}
              pending={pending}
              onToggle={onToggle}
            />
          ))}
          {others.length > 0 && <GroupLabel>Другие пользователи</GroupLabel>}
          {others.map((person) => (
            <PersonRow
              key={person.id}
              person={person}
              checked={selectedIds.has(person.id)}
              pending={pending}
              onToggle={onToggle}
            />
          ))}
          {inProject.length === 0 && others.length === 0 && (
            <div className="px-2 py-1.5 text-[12.5px] text-muted-foreground">
              Никого не найдено
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  )
}

function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-1.5 py-1 text-[11px] font-normal text-muted-foreground/70">
      {children}
    </div>
  )
}

function PersonRow({
  person,
  checked,
  pending,
  onToggle,
}: {
  person: Person
  checked: boolean
  pending: boolean
  onToggle: (person: Person) => void
}) {
  return (
    <button
      type="button"
      onClick={() => onToggle(person)}
      disabled={pending}
      className={cn(
        "flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-[12.5px] leading-none font-normal transition-colors hover:bg-accent focus:bg-accent focus:outline-none",
        checked && "bg-accent/40",
        pending && "opacity-60",
      )}
    >
      <span
        className={cn(
          "size-3.5 shrink-0 rounded-[3px] border flex items-center justify-center",
          checked
            ? "border-primary bg-primary text-primary-foreground"
            : "border-muted-foreground/30",
        )}
      >
        {checked && <HugeiconsIcon icon={Tick02Icon} strokeWidth={3} className="size-2.5 text-white" />}
      </span>
      <span className="truncate flex-1 text-foreground/90">{person.name}</span>
    </button>
  )
}



function pluralizeMembers(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return "участник"
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return "участника"
  return "участников"
}
